import dayjs from "dayjs";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  ChevronUp,
  Equal,
} from "lucide-react";
import { JobOrderStatus } from "@/routes/(planner-screens)/utils/constants";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getInitials } from "@/routes/(planner-screens)/utils/utils";
import { TechnicianType } from "@/hooks/services/pps/types/job-orders.type";
import { getValueByPath } from "@/components/datatable/helper";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const stripTrailingSlash = (value: string): string => value.replace(/\/$/, "");

/**
 * VITE_WORKCHESTRA_BASE_URL with any trailing slash stripped. Every service's
 * base.ts appends its own "/api[...]" suffix to this, so an accidental
 * trailing slash in the env value would otherwise bake a literal "//" into
 * the middle of the baseURL.
 *
 * Named "origin" (not "base URL") to stay distinct from
 * lib/widget-utils.ts's file-local getWorkchestraBaseUrl(), which falls
 * back to a hardcoded default when the env var is unset instead of "".
 */
export function getWorkchestraOrigin(): string {
  return stripTrailingSlash(import.meta.env.VITE_WORKCHESTRA_BASE_URL || "");
}

export function removeProperty(obj: any, prop: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [prop]: omit, ...res } = obj;
  return res;
}

export function debounce(callback: any, delay: number) {
  let timer: NodeJS.Timeout;

  return (args: any) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      callback(args);
    }, delay);
  };
}

export function GetRedirectUrl(
  type: string,
  id: number,
  formId: string,
): string {
  const rawBaseUrl =
    type === "CSR"
      ? import.meta.env.VITE_CSR_BASE_URL
      : import.meta.env.VITE_WORKCHESTRA_BASE_URL;
  if (!rawBaseUrl || rawBaseUrl.trim() === "") {
    return "base URL is not configured in environment variables";
  }
  const baseUrl = stripTrailingSlash(rawBaseUrl);

  let url = "";
  if (type === "CRR") {
    url = `${baseUrl}/eforms/formbuilder/view/CRR Form/${id}`;
  }
  if (type === "CSR") {
    url = `${baseUrl}/csrs/${formId}`;
  }
  return url;
}

export const groupDataBy = (array: any[], key: string): any[] => {
  const groupedData: { [key: string]: string[] } = {};

  if (array && array.length > 0) {
    if (key.includes(".")) {
      const keys = key.split(".");
      array.forEach((item) => {
        let value = item;
        for (const k of keys) {
          if (value && k in value) {
            value = value[k];
          } else {
            value = null;
            break;
          }
        }

        if (typeof value === "string") {
          const groupKeys = value
            .toString()
            .split(" ")
            .map((i: any, idx: number) => (idx === 0 ? i.toLowerCase() : i))
            .join("");

          if (!groupedData[groupKeys]) {
            groupedData[groupKeys] = [];
          }
          groupedData[groupKeys].push(item);
        }
      });
    } else {
      array.forEach((item) => {
        if (item[key]) {
          // make camelcase
          const groupKeys = item[key]
            .toString()
            .split(" ")
            .map((i: any, idx: number) => (idx === 0 ? i.toLowerCase() : i))
            .join("");

          if (!groupedData[groupKeys]) {
            groupedData[groupKeys] = [];
          }
          groupedData[groupKeys].push(item);
        }
      });
    }
  }

  return Object.keys(groupedData).map((groupKey) => ({
    groupKey,
    items: groupedData[groupKey],
  }));
};

export const capitalize = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const flattenArray = (array = []) => {
  const newArray: any[] = [];

  if (array.length) {
    array.forEach((item) => {
      if (Array.isArray(item)) {
        newArray.push(...flattenArray(item));
      } else {
        newArray.push(item);
      }
    });
  }
  return newArray;
};

export const dayjsLocalizer = (dayjsInstance: typeof dayjs) => ({
  format: (date: Date, formatString: string) =>
    dayjsInstance(date).format(formatString),
  parse: (value: string, formatString: string) =>
    dayjsInstance(value, formatString).toDate(),
  startOfWeek: () => dayjsInstance().startOf("week").toDate(),
  endOfWeek: () => dayjsInstance().endOf("week").toDate(),
});

export const showPriorityIcon = (priority: number) => {
  switch (priority) {
    case 1:
      return <ChevronsUp className="aero:!text-destructive" />;
    case 2:
      return <ChevronUp className="aero:!text-red-500" />;
    case 4:
      return <ChevronDown className="aero:!text-blue-600" />;
    case 5:
      return <ChevronsDown className="aero:!text-blue-300" />;
    default:
      return <Equal className="aero:!text-gray-500" />;
  }
};

export const showPriorityText = (priority: number) => {
  switch (priority) {
    case 1:
      return "Critical";
    case 2:
      return "High";
    case 4:
      return "Low";
    case 5:
      return "Minor";
    default:
      return "Medium";
  }
};

export function BadgeStatus({ status }: { status: string }) {
  let variant:
    | "default"
    | "outline"
    | "secondary"
    | "destructive"
    | "warning"
    | "success" = "outline";
  let className = "";

  switch (status) {
    case JobOrderStatus.NotStarted:
      variant = "outline";
      className =
        "aero:bg-slate-50 aero:!text-slate-600 aero:hover:bg-slate-50";
      break;

    case JobOrderStatus.InProgress:
      variant = "outline";
      className = "aero:bg-blue-50 aero:!text-blue-600 aero:hover:bg-blue-50";
      break;

    case JobOrderStatus.JobStarted:
      variant = "outline";
      className =
        "aero:bg-emerald-50 aero:!text-emerald-600 aero:hover:bg-emerald-50";
      break;

    case JobOrderStatus.RepairInProgress:
      variant = "outline";
      className = "aero:bg-teal-50 aero:!text-teal-600 aero:hover:bg-teal-50";
      break;

    case JobOrderStatus.EvaluationInProgress:
      variant = "outline";
      className = "aero:bg-cyan-50 aero:!text-cyan-600 aero:hover:bg-cyan-50";
      break;

    case JobOrderStatus.WSI:
      variant = "outline";
      className =
        "aero:bg-violet-50 aero:!text-violet-600 aero:hover:bg-violet-50";
      break;

    case JobOrderStatus.Completed:
      variant = "outline";
      className =
        "aero:bg-green-50 aero:!text-green-600 aero:hover:bg-green-50";
      break;

    case JobOrderStatus.PendingARCGeneration:
      variant = "outline";
      className = "aero:bg-red-50 aero:!text-red-600 aero:hover:bg-red-50";
      break;

    case JobOrderStatus.PendingCustomerApproval:
      variant = "outline";
      className = "aero:bg-rose-50 aero:!text-rose-600 aero:hover:bg-rose-50";
      break;

    case JobOrderStatus.PendingCSDQuotation:
      variant = "outline";
      className = "aero:bg-pink-50 aero:!text-pink-600 aero:hover:bg-pink-50";
      break;

    case JobOrderStatus.PendingSubcon:
      variant = "outline";
      className =
        "aero:bg-fuchsia-50 aero:!text-fuchsia-600 aero:hover:bg-fuchsia-50";
      break;

    case JobOrderStatus.PendingMaterial:
      variant = "outline";
      className =
        "aero:bg-orange-50 aero:!text-orange-600 aero:hover:bg-orange-50";
      break;

    default:
      variant = "outline";
      className = "aero:bg-gray-50 aero:!text-gray-600 aero:hover:bg-gray-50";
      break;
  }

  return (
    <Badge variant={variant} className={className}>
      {status}
    </Badge>
  );
}
// this will be deleted
export function TechnicianAvatars({
  technicians = [],
}: {
  technicians: TechnicianType[];
}) {
  return (
    <div className="aero:flex aero:-space-x-2">
      {technicians.slice(0, 3).map((tech: any, index: number) => (
        <Tooltip key={index}>
          <TooltipTrigger>
            <Avatar className="aero:h-7 aero:w-7 aero:border-2 aero:border-background aero:transition-transform aero:hover:z-10 aero:hover:scale-110">
              <AvatarImage src={tech.avatar} alt={tech.name} />
              <AvatarFallback className="aero:bg-brand-primary/20 aero:!text-xs aero:!text-brand-primary">
                {getInitials(tech.name)}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>{tech.name}</TooltipContent>
        </Tooltip>
      ))}
      {technicians.length > 3 && (
        <Tooltip>
          <TooltipTrigger>
            <Avatar className="aero:flex aero:h-7 aero:w-7 aero:items-center aero:justify-center aero:border-2 aero:border-background aero:bg-gray-200 aero:!text-xs aero:!font-medium aero:transition-transform aero:hover:z-10 aero:hover:scale-110">
              +{technicians.length - 3}
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            {technicians
              .slice(3)
              .map((tech: any) => tech.name)
              .join(", ")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export function InitialAvatar({
  data = [],
  nameKey,
}: {
  data: any[];
  nameKey: string;
}) {
  return (
    <div className="aero:flex aero:-space-x-2">
      {data.slice(0, 3).map((item: any, index: number) => {
        const name = item?.[nameKey] ?? "-";
        // const avatar = item?.[avatarKey] ?? "";
        return (
          <Tooltip key={index}>
            <TooltipTrigger>
              <Avatar className="aero:h-7 aero:w-7 aero:border-2 aero:border-background aero:transition-transform aero:hover:z-10 aero:hover:scale-110">
                {/* <AvatarImage src={avatar} alt={name} /> */}
                <AvatarFallback className="aero:bg-brand-primary/20 aero:!text-xs aero:!text-brand-primary">
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>
            </TooltipTrigger>
            <TooltipContent>{name}</TooltipContent>
          </Tooltip>
        );
      })}
      {data.length > 3 && (
        <Tooltip>
          <TooltipTrigger>
            <Avatar className="aero:flex aero:h-7 aero:w-7 aero:items-center aero:justify-center aero:border-2 aero:border-background aero:bg-gray-200 aero:!text-xs aero:!font-medium aero:transition-transform aero:hover:z-10 aero:hover:scale-110">
              +{data.length - 3}
            </Avatar>
          </TooltipTrigger>
          <TooltipContent>
            {data
              .slice(3)
              .map((item: any) => item?.[nameKey])
              .join(", ")}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export const renderCell = (row: any, column: any) => {
  const columnName =
    typeof row.original[column.name] === "object" &&
    column?.accessorPath !== "" &&
    column?.render?.type !== "initial-avatar"
      ? getValueByPath(row.original, column.accessorPath)
      : row.original[column.name];

  if (!column?.render?.type) {
    return columnName || "-";
  }

  switch (column.render.type) {
    case "date":
      return columnName
        ? dayjs(columnName).format(column.render.type?.format || "YYYY-MM-DD")
        : "-";
    case "time":
      return columnName
        ? dayjs(dayjs(columnName, "HH:mm:ss").toDate()).format(
            column.render.type?.format || "HH:mm",
          )
        : "-";
    case "initial-avatar":
      return (
        <InitialAvatar
          data={columnName || []}
          nameKey={column.render.nameKey}
        />
      );

    default:
      return columnName || "-";
  }
};

export const toastClasses = ({
  type = "success",
}: {
  type: "success" | "error" | "info" | "warning";
}): {
  className: string;
  descriptionClassName: string;
} => {
  const typeClasses = {
    success: {
      className: "aero:!bg-green-50 aero:!text-green-700",
      descriptionClassName: "aero:!text-green-700",
    },
    error: {
      className: "aero:!bg-red-50 aero:!text-red-700",
      descriptionClassName: "aero:!text-red-700 ",
    },
    info: {
      className: "aero:!bg-blue-50 aero:!text-blue-700",
      descriptionClassName: "aero:!text-blue-700 ",
    },
    warning: {
      className: "aero:!bg-amber-50 aero:!text-amber-700",
      descriptionClassName: "aero:!text-amber-700 ",
    },
  };
  return typeClasses[type];
};

export const getUserInitials = (name?: string): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "U";

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "U";

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return parts[0].substring(0, 2).toUpperCase();
};

export const lowercaseFirst = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
};
